import {SELECTION_PALETTE} from '../lib/selection-palette';

/**
 * The marks the EDITOR draws over a sheet, and the report draws none of.
 *
 * Every one of these is `pointer-events-none` and positioned against the sheet:
 * they say something about what the author is doing — where a drag will land,
 * what a marquee has caught, where the page stops — and none of them is content.
 * They sat inline in `LivePreview`'s render as five anonymous absolute divs
 * among the real geometry, which is what made that component hard to scan: the
 * chrome and the document were the same shape on the page.
 *
 * Grouped here rather than one file each because they are one idea — editor
 * chrome — and each is a few lines. Same reasoning as `CELL_SCAFFOLDING`: this
 * has to read as part of the tool, never as a rule the author drew.
 */

/** The centre line a dragged element snaps to. */
export function SnapGuide({x}: {x: number}) {
  return (
    <div
      className="pointer-events-none absolute inset-y-0"
      style={{
        left: x - 0.5,
        width: 1,
        backgroundColor: SELECTION_PALETTE.selected,
        zIndex: 50,
      }}
    />
  );
}

/** The rubber-band rectangle a drag-select is sweeping. */
export function MarqueeBox({rect}: {rect: {x0: number; y0: number; x1: number; y1: number}}) {
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        border: `1.5px solid ${SELECTION_PALETTE.marquee}`,
        backgroundColor: SELECTION_PALETTE.marqueeFill,
        borderRadius: 2,
        left: Math.min(rect.x0, rect.x1),
        top: Math.min(rect.y0, rect.y1),
        width: Math.abs(rect.x1 - rect.x0),
        height: Math.abs(rect.y1 - rect.y0),
        zIndex: 60,
      }}
    />
  );
}

/** The box around a multi-element selection, with what it is called. */
export function SelectionBox({
  box,
  label,
}: {
  box: {minX: number; minY: number; maxX: number; maxY: number};
  label?: string;
}) {
  return (
    <div
      className="absolute pointer-events-none rounded-md border border-dashed border-indigo-400 bg-indigo-500/[0.04]"
      style={{
        left: box.minX - 6,
        top: box.minY - 6,
        width: box.maxX - box.minX + 12,
        height: box.maxY - box.minY + 12,
        zIndex: 5,
      }}
    >
      {label && (
        <span className="absolute -top-5 left-0 rounded-md bg-indigo-500 px-1.5 py-0.5 text-[9px] font-medium text-white leading-none whitespace-nowrap shadow-sm">
          {label}
        </span>
      )}
    </div>
  );
}

/** The band a page header occupies, tinted so the author can see its extent. */
export function HeaderBandMark({height}: {height: number}) {
  return (
    <div
      className="pointer-events-none absolute"
      style={{
        top: 0,
        left: 0,
        right: 0,
        height,
        backgroundColor: 'rgba(124,58,237,0.06)',
        borderBottom: '2px solid rgba(124,58,237,0.45)',
        zIndex: 4,
      }}
    />
  );
}
